import { otherApiClient } from ".";
/**
 * 내 구독 현황 조회.
 *
 * 이용권(`GET /memberships`)과 달리 unexpired/expired 분리 없이 flat 배열로 오므로
 * 구독중/구독종료 탭 분리는 status 기준으로 FE 가 파생한다 (`splitSubscriptionsByStatus`).
 *
 * ⚠️ 응답 최상위가 `{ data: [...] }` 엔벨로프가 아니라 **배열 그 자체**다.
 * (상세 `GET /subscriptions/:id` 는 객체를 그대로 내려준다 — 양쪽 모두 엔벨로프 없음)
 */ export const getMySubscriptions = async ()=>{
    const { data  } = await otherApiClient.market("/subscriptions/me");
    return data;
};
export const getSubscriptionDetail = async (subscriptionId)=>{
    const { data  } = await otherApiClient.market("/subscriptions/".concat(subscriptionId));
    return data;
};
export const getSubscriptionCancelPreview = async (subscriptionId)=>{
    const { data  } = await otherApiClient.market("/subscriptions/".concat(subscriptionId, "/cancel-preview"));
    return data;
};
/** 이미 해지된 구독에 다시 해지를 요청했을 때 409 응답의 message 값 */ export const ALREADY_CANCELED = "ALREADY_CANCELED";
/**
 * 구독 해지 실행.
 * 에러: 400 'reasons should not be empty', 409 ALREADY_CANCELED, 403 Forbidden(본인 구독 아님)
 */ export const postSubscriptionCancel = async (subscriptionId, body)=>{
    const { data  } = await otherApiClient.market.post("/subscriptions/".concat(subscriptionId, "/cancel"), body);
    return data;
};
// ── 구독 해지 취소(cancel-revert) ──
/** 되돌리기 가능 기간이 지났을 때 409 응답의 message 값 */ export const REVERT_WINDOW_CLOSED = "REVERT_WINDOW_CLOSED";
/**
 * 해지 예약/완료 상태를 원래 구독으로 되돌린다.
 * 에러: 409 REVERT_WINDOW_CLOSED(되돌리기 기간 만료), 403 Forbidden(본인 구독 아님)
 */ export const postSubscriptionCancelRevert = async (subscriptionId)=>{
    const { data  } = await otherApiClient.market.post("/subscriptions/".concat(subscriptionId, "/cancel-revert"));
    return data;
};
/**
 * 결제 페이지용 구독 상품 조회.
 * 경로는 단수형 `subscription`이다 (`/subscriptions/plans/:id`는 존재하지 않음).
 */ export const getSubscriptionPlan = async (planId)=>{
    const { data  } = await otherApiClient.market("/subscription/plans/".concat(planId));
    return data;
};
/**
 * 월구독 화면 금액의 단일 파생식.
 *
 * 청구 단가는 isResubscribe로 갈린다 — 첫 구독은 판매가(salePrice), 재구독은
 * 첫 구독 할인 혜택을 이미 받았으므로 정가(regularPrice)다. 재구독은 정가가
 * 그대로 청구되므로 사이드바 할인 행이 0이 되어 숨겨진다.
 * 체험이 적용되면 이번 회차 청구가 사라지므로 '매달 청구액'과 '이번 결제액'을 분리해
 * 사이드바와 주문상품 row가 같은 값을 쓰게 한다.
 */ export const getSubscriptionCharge = (plan)=>{
    // 재구독은 첫 구독 할인 혜택을 이미 받았으므로 정가로 청구한다
    const unitPrice = plan.isResubscribe ? plan.regularPrice : plan.salePrice;
    // 매달 청구액 (주문상품 row의 '다음 결제')
    const recurringPrice = unitPrice;
    // 이번 결제 — 체험이 적용되면 즉시 청구가 없다
    const currentCharge = plan.trialApplied ? 0 : unitPrice;
    // 사이드바 '할인' 행. 0이면 행을 숨긴다 (재구독은 항상 0)
    const discount = plan.regularPrice - unitPrice;
    return {
        recurringPrice,
        currentCharge,
        discount
    };
};
/**
 * 빌링 등록창(requestBillingAuth)에 넘길 customerKey 조회. 없으면 서버가 생성해 내려준다.
 *
 * 유저 id 같은 FE 값을 쓰면 안 된다. 등록창은 임의 값도 받아주지만, 이어지는
 * `POST /billing-keys`에서 서버가 자기가 소유한 customerKey로 토스에 조회하므로
 * 짝이 맞지 않으면 빌링키 발급이 실패한다.
 */ export const getBillingCustomerKey = async ()=>{
    const { data  } = await otherApiClient.market("/billing-keys/customer-key");
    return data;
};
/**
 * authKey를 빌링키로 교환한다. 결제는 일어나지 않는다.
 *
 * customerKey는 body에 넣지 않는다 — 서버가 `GET /billing-keys/customer-key`로 소유한 값을 쓴다.
 */ export const postBillingKey = async (body)=>{
    const { data  } = await otherApiClient.market.post("/billing-keys", body);
    return data;
};
/**
 * 월구독 가입. 체험이 아니면 이 호출 안에서 첫 회차 결제까지 끝난다.
 *
 * 일회용 결제와 달리 **승인 후 별도 confirm 호출이 없다.**
 * 결제 실패(402)면 유저 관점으로는 미가입이고, 구독 로우가 status=pending으로 남아
 * 재요청 때 재사용되므로 그대로 다시 시도하면 된다.
 */ export const postSubscription = async (body)=>{
    const { data  } = await otherApiClient.market.post("/subscriptions", body);
    return data;
};
/**
 * 구독 결제수단 교체. 서버가 authKey 로 빌링키를 발급해 교체까지 한다 —
 * 가입과 달리 `postBillingKey` 를 먼저 부르면 authKey 가 소진돼 실패한다.
 *
 * ⚠️ 200 이 결제 성공을 뜻하지 않는다. past_due 는 새 카드로 즉시 재결제를 시도하는데
 * 실패해도 200 이며, 성공 여부는 응답 status 로만 알 수 있다(active 복귀 = 성공).
 *
 * 에러: 400(authKey 검증·발급 실패), 403(타인 소유), 404(구독 없음)
 */ export const patchSubscriptionBillingKey = async (subscriptionId, body)=>{
    const { data  } = await otherApiClient.market.patch("/subscriptions/".concat(subscriptionId, "/billing-key"), body);
    return data;
};
